import type { Product } from '../insurance-products/index.ts'

/**
 * What happens to the existing contracts of a retired product. New business is closed for every retired product, and
 * their existing contracts are neither endorsed nor renewed.
 */
type ExistingContractsHandling = 'terminate-at-expiry' | 'none'

export type RetiredProduct = {
  existingContracts: ExistingContractsHandling
  /**
   * The products that take its place for new business. A duplicate of one of its quotes is allocated to one of them, and
   * the duplication maps its answers onto each. The first one is the product a link built for it now selects.
   */
  successors: readonly Product[]
}

// Empty since Wakam relaunched mrpw, fr-rcpw and es-mrpw, which it had stopped carrying on 1 October 2026
const RETIRED_PRODUCTS = {} as const satisfies Partial<Record<Product, RetiredProduct>>

const retiredProductsByProduct: Partial<Record<Product, RetiredProduct>> = RETIRED_PRODUCTS

type RetiredProducts = typeof RETIRED_PRODUCTS

export type RetiredProductWithSuccessors = {
  [P in keyof RetiredProducts]: RetiredProducts[P]['successors'] extends readonly [] ? never : P
}[keyof RetiredProducts]

export type RetiredProductSuccessor<P extends RetiredProductWithSuccessors> = RetiredProducts[P]['successors'][number]

export function isRetiredProductWithSuccessors(product: Product): product is RetiredProductWithSuccessors {
  return (retiredProductsByProduct[product]?.successors.length ?? 0) > 0
}

// Typed through a mapped type rather than read from RETIRED_PRODUCTS, so that an empty list still compiles
const retiredProductsWithSuccessors: {
  [P in RetiredProductWithSuccessors]: { successors: readonly RetiredProductSuccessor<P>[] }
} = RETIRED_PRODUCTS

export function getRetiredProductSuccessors<P extends RetiredProductWithSuccessors>(
  product: P,
): readonly RetiredProductSuccessor<P>[] {
  return retiredProductsWithSuccessors[product].successors
}

/**
 * The checks every behaviour of a retired product reads. The code they gate takes them as a dependency, so that a test
 * retires a product of its own choice, whatever the real list holds.
 */
export type RetiredProductRegistry = {
  isRetiredProduct: (product: Product) => boolean
  includesRetiredProduct: (selectedProducts: readonly Product[]) => boolean
  /** The product a link built for a retired product now selects, or undefined when it is not retired or has no successor. */
  getRetiredProductSuccessor: (product: Product) => Product | undefined
  isRetiredProductTerminatedAtExpiry: (product: Product) => boolean
}

const IS_TERMINATED_AT_EXPIRY: Record<ExistingContractsHandling, boolean> = {
  'terminate-at-expiry': true,
  none: false,
}

export function createRetiredProductRegistry(
  retiredProducts: Partial<Record<Product, RetiredProduct>>,
): RetiredProductRegistry {
  const isRetiredProduct = (product: Product): boolean => retiredProducts[product] !== undefined
  return {
    isRetiredProduct,
    includesRetiredProduct: (selectedProducts) => selectedProducts.some(isRetiredProduct),
    getRetiredProductSuccessor: (product) => retiredProducts[product]?.successors[0],
    isRetiredProductTerminatedAtExpiry: (product) => {
      const retiredProduct = retiredProducts[product]
      return retiredProduct !== undefined && IS_TERMINATED_AT_EXPIRY[retiredProduct.existingContracts]
    },
  }
}

export const retiredProductRegistry: RetiredProductRegistry = createRetiredProductRegistry(retiredProductsByProduct)

export const { isRetiredProduct, isRetiredProductTerminatedAtExpiry } = retiredProductRegistry
